// src/pages/Help.js
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaClock,
  FaCalendarAlt,
  FaUsers,
  FaHeadset,
  FaChartBar,
  FaUserTie,
  FaBell,
  FaCogs,
  FaMapMarkerAlt,
  FaTasks, // ✅ ADD THIS
} from "react-icons/fa";

import { LuBaggageClaim } from "react-icons/lu";
import { MdOutlineAttachMoney } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSubscription } from "../hooks/useSubscription";
import { useAuth } from "../Context/AuthContext";
import { db } from "../firebase/config";
import {
  addDoc,
  collection,
  serverTimestamp,
  doc,
  getDoc,
} from "firebase/firestore";
import WelcomeTour from "../components/WelcomeTour";

import "../styles/Help.css";

// Always show these, regardless of plan:
const CORE_ALWAYS = new Set([
  "employer-profile",
  "notifications",
  "settings",
  "job-sites",
]);

// Which help topics each plan is allowed to see
const PLAN_TOPIC_ACCESS = {
  Basic: new Set([...CORE_ALWAYS, "time-tracking", "scheduling", "employees"]),
  Standard: new Set([
    ...CORE_ALWAYS,
    "time-tracking",
    "scheduling",
    "employees",
    "leave",
    "expenses",
    "tasks", // ✅ ADD
  ]),
  Premium: new Set([
    ...CORE_ALWAYS,
    "time-tracking",
    "scheduling",
    "employees",
    "leave",
    "expenses",
    "training",
    "analytics",
    "tickets",
    "payroll",
    "tasks", // ✅ ADD
  ]),
  // Standalone products
  Training: new Set([...CORE_ALWAYS, "training", "analytics"]),
  Helpdesk: new Set([...CORE_ALWAYS, "tickets", "analytics"]),
};

// Helper: get the allowed topic ids for a given plan
const allowedIdsForPlan = (planName) =>
  PLAN_TOPIC_ACCESS[planName] || PLAN_TOPIC_ACCESS.Basic;

/** ──────────────────────────────────────────────────────────────
 *  HELP TOPICS – EMPLOYER-FRIENDLY LANGUAGE ONLY
 *  (No field names, no Firestore paths, no code jargon)
 *  targetTab must match AdminDashboard tab keys.
 *  ────────────────────────────────────────────────────────────── */
const TOPICS = [
  {
    id: "time-tracking",
    path: "/attendance-tracking",
    icon: <FaClock />,
    title: "Time Tracking",
    summary:
      "Review who worked, apply early/late and overtime rules, fix mistakes with a clear audit trail, and keep hours ready for payroll.",
    targetTab: "attendance",
    tags: [
      "Approvals",
      "Timesheets",
      "Overtime",
      "Policies",
      "Audit",
      "Settings",
    ],
    steps: [
      "Open the Time Tracking page from your dashboard. Use the Filter button in the top-right to narrow what you see by Employee, Status (On Time, Late, Early, On Leave), whether the session is currently clocked in or completed, and by date range.",
      "Use the Action menu in the top-left to open Settings, jump to Timesheets, or add a manual time entry. Timesheets give you a weekly, approval-focused view, while Time Tracking shows the individual punches.",
      "At the top of the table, check the Hours overview bar. It shows Total, Regular, and Overtime hours for the entries that match your current filters. This uses your Time Tracking overtime rules and includes completed paid-leave entries that have hours.",
      "Click Settings in the Action menu to configure Time Tracking rules. You can turn Early Clock-In and Late Grace on or off, choose how many minutes before and after the shift are allowed, and decide how overtime is detected (based on total hours in a period or daily hours).",
      "As employees clock in and out, their entries appear automatically. All dates and times are shown in your company time zone so they line up with Scheduling, Payroll, and Analytics.",
      "If an employee makes a mistake (for example, forgets to clock out or the time is wrong), click Edit on that row. Adjust the clock-in and/or clock-out times and add a short reason so there is a clear change history. The system recalculates hours automatically.",
      "If someone forgets to clock out and is still marked as clocked in, click Force Clock-Out on their row. Choose a reason and confirm. The system records who forced the clock-out, when it happened, and why.",
      "To record time that never went through the normal clock-in/clock-out flow (for example, missed punches or approved off-site work), use Add Time from the Action menu. Pick the employee, set clock-in and clock-out, and give a reason. The app checks for overlaps and blocked weeks before saving.",
      "When you’re ready to review hours for payroll, use the Timesheets view. There you can see hours by week and employee, approve or unapprove weeks, and lock weeks so entries can’t be changed without unlocking them first.",
    ],
    faqs: [
      [
        "How does the system decide who is On Time, Early, or Late?",
        "The status is decided at clock-in based on the Time Tracking settings you choose. If an employee clocks in within your early window before the shift start, they are flagged as Early Clock-in. If they clock in after the start but within your Late Grace window, they are treated as On Time. If they clock in after the grace period, they are marked as Late Clock-in.",
      ],
      [
        "Where do I change my early, late, and overtime rules?",
        "On the Time Tracking page, open the Action menu and choose Settings. From there, you can turn Early Clock-In and Late Grace on or off, set how many minutes are allowed, and decide how overtime is detected—either after a set number of hours in a period (such as 40 or 80 hours) or after more than a certain number of hours in a single day.",
      ],
      [
        "What does the Hours overview bar show?",
        "The Hours overview bar summarizes completed sessions that match your current filters. It shows Total hours, Regular hours, and Overtime hours based on your Time Tracking settings. Paid-leave entries with hours are included so your totals reflect both work and paid time off.",
      ],
      [
        "Why can’t I edit or force clock-out for some entries?",
        "If an entry is part of a week that has already been approved and locked in Timesheets, edits and forced clock-outs are blocked. Unlock the relevant week in Timesheets first if you need to make corrections, then return to Time Tracking to adjust the entry.",
      ],
      [
        "Why is the Hours overview bar not showing?",
        "The Hours overview bar only appears when there are matching, completed entries after your filters are applied. If there are no matches, or everything in view is still clocked in with no clock-out time, you’ll see a 'No matching records found' message instead of the summary.",
      ],
      [
        "Which time zone is used for time entries?",
        "All dates and times follow your company time zone set in Settings. This keeps Time Tracking, Scheduling, Payroll, and Analytics aligned even if employees are working from different locations.",
      ],
      [
        "How is location shown for clock-ins?",
        "Each entry shows a simple location label where possible, such as city and country when available. You may also see a small GPS or IP tag indicating whether the location came from precise GPS coordinates or an IP-based lookup, so you have basic context on where the clock-in happened.",
      ],
    ],
  },

  {
    id: "scheduling",
    path: "/employer-scheduling",
    icon: <FaCalendarAlt />,
    title: "Scheduling",
    summary:
      "Create weekly schedules for one or many employees, respect approved leave, control maximum hours, compare scheduled vs worked time, and export your plan.",
    targetTab: "Scheduling",
    tags: [
      "Planning",
      "Activities",
      "Hours & Caps",
      "Overview",
      "CSV Export",
      "Leave-aware",
      "Job Sites",
    ],
    steps: [
      "Open the Scheduling page. Use the tabs at the top to switch between Create/Edit, Schedule History, and Weekly Overview.",
      "In the Create/Edit tab, pick a week with the date range picker. The system always builds a full 7-day week. New schedules can’t be created for weeks that are entirely in the past.",
      "Select the Job Site for this schedule. This ensures shifts are tied to the correct location for planning and reporting. If a site is inactive, it won’t be available for new schedules.",

      "Choose whether you’re scheduling a single employee or turning on Multi-assign. Only Active employees are shown. Employees who already have approved paid leave for that week are disabled in Multi-assign so you don’t accidentally schedule over their leave.",
      "For each day, set the shift start and end time or mark the day as an Off Day or Holiday. You’ll see a live breakdown of base hours, paid activities, unpaid activities, and total scheduled hours for the week.",
      "Use activities inside a shift (for example, Training, Meeting, Break, or Lunch). You can add activities, mark each as paid or unpaid, attach a small note, and manage reusable activity types with colors and a “paid by default” setting from the Manage Activity Types button.",
      "Click the Hours & Caps settings (gear icon) to open the Hours & Caps panel. Set company weekly/monthly caps and per-employee weekly/monthly caps. Choose whether caps should hard-block saves when exceeded or just show warnings you can override.",
      "When you save a schedule, the system checks for overlapping schedules and hours caps. For single employees it blocks overlapping weeks. For Multi-assign, it automatically skips employees that already have a conflicting schedule and shows a summary of how many were created and how many were skipped.",
      "Use the Schedule History tab to see existing schedules for each employee. From here you can edit a schedule (which opens it in the Create/Edit panel) or delete it if you no longer need it.",
      "Use the Weekly Overview tab to compare scheduled hours with actual worked hours from Time Tracking. You can filter by employee, move between weeks, see Off/Holiday/Leave days, spot missed shifts, and export the weekly grid as a CSV file for reporting or payroll prep.",
    ],
    faqs: [
      [
        "What are Hours & Caps used for?",
        "Hours & Caps help you stay within your labor budget and internal rules. You can set weekly and monthly limits at both the company and per-employee level. When a new schedule would push you over those limits, the system either blocks the save or shows a warning (depending on your settings) so you don’t accidentally over-schedule staff.",
      ],
      [
        "Why do I need to choose a Job Site?",
        "Job Sites let you assign schedules to a specific location so planning stays organized—especially if you operate multiple sites. It also helps ensure the right employees are scheduled at the right place.",
      ],
      [
        "Why don’t I see a Job Site in the list?",
        "Only active job sites are available for new schedules. If a site was deactivated, reactivate it in Settings → Job Sites.",
      ],

      [
        "What happens when I use Multi-assign?",
        "Multi-assign lets you apply the same weekly pattern to several employees at once. Only Active employees can be selected. If any of the selected employees already have an overlapping schedule for that week, or are on approved paid leave for that week, the system skips just those employees and tells you how many were skipped while creating schedules for the rest.",
      ],
      [
        "How are activities counted in the total hours?",
        "For each day, the system starts with the main shift window you set (start to end). It then adds the duration of any paid activities and subtracts the duration of any unpaid activities (such as unpaid lunch). Off Days and Holidays count as zero. This gives you an accurate total of scheduled paid hours for the day and week.",
      ],
      [
        "How do missed-shift alerts work in Weekly Overview?",
        "A missed shift is flagged when an employee has a scheduled shift for a day, but there is no matching clock-in recorded from Time Tracking by the time the shift has ended (plus a small grace period). Days marked as Off, Holiday, or with approved leave are never treated as missed. When a shift is missed, it shows in the Weekly Overview and can also trigger a missed-shift notification for that employee and date.",
      ],
      [
        "How does scheduling interact with approved leave?",
        "When you pick a week, the system checks approved leave for that period. Employees on approved paid leave for that week are disabled in Multi-assign, and individual days with approved leave show as Paid or Unpaid leave in both the Create/Edit grid and Weekly Overview. Those days cannot be edited into normal working shifts, helping you avoid double-booking employees who are on leave.",
      ],
      [
        "Can I change schedules in the past?",
        "You can edit existing recent schedules from the Schedule History tab if you need to tidy up your records. However, you can’t create a brand-new schedule for a week that is completely in the past. For historical payroll and auditing, we recommend relying mainly on Time Tracking and timesheets, with Scheduling focused on current and future planning.",
      ],
    ],
  },
  {
    id: "tasks",
    path: "/task-manager",
    icon: <FaTasks />,
    title: "Task Manager",
    summary:
      "Create tasks, assign them to employees (single or multi-assign), require photo proof, use checklists, and approve or reject submissions with clear tracking.",
    targetTab: "taskmanager", // ✅ change this ONLY if your AdminDashboard uses a different tab key
    tags: [
      "Assignments",
      "Checklists",
      "Photo Proof",
      "Approvals",
      "Job Sites",
      "Notifications",
      "Tracking",
    ],
    steps: [
      "Open Task Manager from your dashboard. You’ll see KPI cards at the top showing counts like Open, In Progress, Completed, Approved, Rejected, and Overdue.",
      "Use the filters to narrow tasks by Employee, Job Site, Status, Priority, and Review state (Not submitted, Under review, Approved, Rejected).",
      "Click + Create Task to open the create screen.",
      "Enter a task title and optional description. Choose a Task Type and Priority.",
      "Optional: Turn on Add checklist to include step-by-step items for the employee to complete. You can add up to the limit shown in the hint.",
      "Optional: Turn on Require photo proof if the employee must upload proof before submitting.",
      "Under Assign, choose whether to assign a single employee or enable Assign multiple employees.",
      "Select the Job Site and Due Date. All due dates follow your company time zone.",
      "Click Publish Task. Each assigned employee receives a task notification.",
      "When employees submit the task for review, open Task Manager and click the task row to view full details, checklist progress, comments, and proof photos.",
      "If everything looks good, click Approve. If something needs fixing, click Reject and include a reason—this sends it back to the employee to correct and resubmit.",
    ],
    faqs: [
      [
        "What happens when I assign a task to multiple employees?",
        "Oiiko creates one task per employee so progress and proof stay individual. Each employee receives their own notification and review status.",
      ],
      [
        "What does 'Under review' mean?",
        "Under review means the employee submitted the task and it’s waiting for an employer decision (Approve or Reject).",
      ],
      [
        "Why is a task marked as Overdue?",
        "A task becomes overdue when the due date has passed and it hasn’t been approved or archived yet.",
      ],
      [
        "Can I require proof photos?",
        "Yes. Turn on Require photo proof during task creation. Employees must upload a photo before submitting the task.",
      ],
      [
        "How do checklist tasks work?",
        "When checklists are enabled, the employee completes checklist items as they work. You can review checklist progress in the task details view before approving.",
      ],
      [
        "Do employees get notified when I approve or reject?",
        "Yes. Employees receive a notification when a task is assigned, approved, or rejected, including your rejection reason when provided.",
      ],
    ],
  },

  // ✅ Replace ONLY your existing { id: "leave", ... } topic with this updated version
  {
    id: "leave",
    path: "/employer-leave",
    icon: <LuBaggageClaim />,
    title: "Leave & Absence",
    summary:
      "Review time-off requests in one place—approve or reject in seconds, protect your time bank balances, and keep reports accurate with automatic paid-leave time entries.",
    targetTab: "leave",
    tags: [
      "Approvals",
      "Time Bank",
      "Leave Types",
      "Policy",
      "Filters",
      "Attachments",
      "Paid Leave",
      "Time zone",
      "Audit",
    ],
    steps: [
      "Open Leave & Absence from your dashboard. You’ll see requests organized into tabs: Pending, Approved, Rejected, and Cancelled.",
      "Use Pending to review new requests. Each row shows the employee, leave type, time of day (Full Day or Half Day), start/end dates, available hours, and the hours that would be deducted.",
      "Click Filter to narrow results by Employee, Leave Type, or Time of Day, and sort by Start Date (Ascending or Descending). This helps you focus on what matters without losing your place.",
      "If you have a lot of records, use pagination at the bottom to move between pages and choose how many rows you see (25, 50, or 100).",
      "To read the full reason, click the Reason cell. A full-screen reason modal opens so you can review the request without leaving the page.",
      "If a supporting document was uploaded (like a doctor’s note), click 📎 View to open it in the document viewer modal.",
      "Approve or Reject a Pending request. When approved, the employee is notified, Time Bank hours can be deducted automatically (if enabled), and the decision is saved to Leave History for reporting.",
      "If Time Bank is enabled and the request would exceed the employee’s available hours, Approve is disabled to prevent overspending. You can adjust balances first in Leave Settings if needed.",
      "After approval, Oiiko automatically creates matching paid-leave time entries for each day in the approved range. This keeps Time Tracking, totals, and payroll-style reports accurate without the employee needing to clock in.",
      "In the Approved tab, you can cancel an approved leave (before it starts). Cancelling refunds any deducted Time Bank hours and removes the auto-created paid-leave time entries so your totals stay consistent.",
      "Cancellation is protected: if the leave has already started or already passed (based on your company time zone), the system blocks cancellation to preserve accurate history.",
      "Click the Leave Settings (gear icon) to manage Balances, Leave Types, and Policy. This is your control center for how leave works across your company.",
      "In Leave Settings → Balances: select an employee and leave type, then add hours, deduct hours, or set an exact balance. A balance preview shows what the employee currently has.",
      "In Leave Settings → Leave Types: create new leave types, set colors, and enable/disable types. Disabling hides the type from new requests but preserves history and balances.",
      "In Leave Settings → Policy: set how many hours count as a Full Day for your company (for example 8 hours). Half-day requests still use the employee’s custom times when provided.",
    ],
    faqs: [
      [
        "What request statuses are used?",
        "Requests move through Pending, Approved, Rejected, and Cancelled. Pending needs a decision. Approved is confirmed time off. Rejected is declined. Cancelled is an approved request that was reversed before it started.",
      ],
      [
        "Which time zone does leave use?",
        "Leave dates, “today” checks (like cancellation protection), and related timestamps follow your company time zone. That keeps leave aligned with Scheduling, Time Tracking, and reports.",
      ],
      [
        "Why is Approve disabled for some requests?",
        "If Time Bank is enabled for that employee and the requested hours are greater than their available balance for that leave type, Approve is disabled. Add more hours or adjust the balance in Leave Settings before approving.",
      ],
      [
        "How are requested hours calculated?",
        "Full Day uses your company’s Full-day hours policy. Half Day defaults to a smaller amount, but if the employee provides custom start/end times, the system uses that duration to calculate hours.",
      ],
      [
        "What happens when I approve a request?",
        "The employee is notified, the decision is saved to Leave History, Time Bank hours are deducted (if enabled), and matching paid-leave time entries are created so reports and totals include approved paid leave accurately.",
      ],
      [
        "What happens when I cancel an approved leave?",
        "If the leave has not started yet, cancelling refunds deducted Time Bank hours (if used) and removes the auto-created paid-leave time entries to keep totals and payroll-style reports correct.",
      ],
      [
        "Why can’t I cancel an approved leave?",
        "Cancellation is blocked if the leave has already started or already passed. This prevents changing history after the fact and keeps reporting accurate.",
      ],
      [
        "Can I manage leave types like Vacation or Sick Leave?",
        "Yes. In Leave Settings → Leave Types, you can add new types, pick colors, and enable/disable types. Disabling removes it from new requests while keeping history intact.",
      ],
      [
        "Can I view supporting documents before deciding?",
        "Yes. If a request includes an attachment, click 📎 View to open it in the document viewer modal.",
      ],
      [
        "Where do approved leave records show up later?",
        "Approved (and other decisions) are written into Leave History for reporting and auditing, so you can review what happened even after requests move between tabs.",
      ],
    ],
  },

  {
    id: "expenses",
    path: "/employer-expense",
    icon: <MdOutlineAttachMoney />,
    title: "Expense Management",
    summary:
      "Review employee expenses with receipts, approve or request changes, and keep spending under control with monthly and yearly budgets that roll over automatically in your company time zone.",
    targetTab: "Expense",
    tags: [
      "Receipts",
      "Approvals",
      "Budgets",
      "Policy",
      "Settings",
      "Filters",
      "Notifications",
      "Audit",
      "Time zone",
    ],
    steps: [
      // 1 — Submissions
      "Employees submit expenses with an expense type, amount, date, description, and an optional receipt (image or PDF).",

      // 2 — Your review workspace (tabs)
      "Open Expense Management from your dashboard. Use the status tabs to review requests by stage: Pending, Needs Changes, Approved, and Rejected.",

      // 3 — View receipts quickly
      "If a receipt was uploaded, click View to open it in a receipt viewer so you can verify the expense without leaving the page.",

      // 4 — Decisions (Pending)
      "For Pending requests, you can Approve, Reject, or Request Changes. Approve accepts the expense. Reject declines it. Request Changes sends it back to the employee so they can edit and resubmit.",

      // 5 — Notes for Reject / Needs Changes
      "When you Reject or Request Changes, you’ll be asked to add a note. This note is sent to the employee so they understand what to fix or why it was declined.",

      // 6 — Employee notifications
      "Employees automatically receive a notification when an expense is Approved, Rejected, or marked Needs Changes, so you don’t have to chase emails.",

      // 7 — Filter & Sort
      "Click Filter to narrow results by Employee and Expense Type, then sort by date (Ascending or Descending). This makes it easy to focus on one person, one category, or a specific time window of spending.",

      // 8 — Saved view
      "Your selected tab, filters, sort order, and page size are remembered. When you return later, the Expense page loads with your last view so you can pick up where you left off.",

      // 9 — Pagination
      "If you have many expenses, use the pagination controls to move through pages and change how many rows you see (25, 50, or 100 per page).",

      // 10 — Budget setup (first run banner)
      "If Expense Management is new for your company, you’ll see a setup reminder to configure budgets. You can dismiss it or open settings right away.",

      // 11 — Budget cards
      "At the top, budget cards show Monthly and Yearly budgets, how much has been used, and how much remains. When a budget reaches zero, you’ll see a warning and a quick link to increase the limit.",

      // 12 — Budget Settings modal
      "Open the settings gear and choose Budget Settings to set your monthly and yearly limits. Enter 0 if you don’t want a cap for that period.",

      // 13 — Auto rollover by company time zone
      "Budgets automatically roll over at the start of a new month or year based on your company time zone, so your periods line up with your real operations.",

      // 14 — Budget impact on approval
      "When you approve an expense, it reduces your remaining Monthly and Yearly budgets. Pending, Needs Changes, and Rejected requests do not affect your totals.",

      // 15 — Exceeding budgets
      "If approving an expense would push you beyond your budget, you’ll see a warning so you can make an informed decision. If needed, you can increase the budget in Budget Settings.",

      // 16 — Expense types & payment methods
      "From the settings gear, open Expense Types & Payment Methods to control what employees can submit and which payment methods are available (for example, Company Card, Reimbursable, Cash).",

      // 17 — Enable/disable options
      "In Expense Types & Payment Methods, you can add new options, remove old ones, and toggle items on or off. Disabled options disappear from employee submission choices while keeping your history intact.",
    ],
    faqs: [
      [
        "What statuses are used for expenses?",
        "Expenses move through four statuses: Pending (awaiting review), Needs Changes (employee must update and resubmit), Approved (accepted), and Rejected (declined).",
      ],
      [
        "How do I ask an employee to fix an expense instead of rejecting it?",
        "Use Request Changes on a Pending expense. Add a note describing what needs to be corrected (missing receipt, unclear description, wrong date, etc.). The employee will be notified and can resubmit.",
      ],
      [
        "What reduces my remaining budget?",
        "Only Approved expenses reduce your remaining Monthly and Yearly budgets. Pending, Needs Changes, and Rejected requests do not affect budgets.",
      ],
      [
        "What happens if I change my monthly or yearly budget later?",
        "Updating a budget keeps what has already been used in the current period and recalculates the remaining amount from your new limit. It does not wipe your spending history for the current month or year.",
      ],
      [
        "How does automatic rollover work?",
        "At the start of a new calendar month or year (based on your company time zone), remaining budgets reset to the full budget amounts you set for that period.",
      ],
      [
        "Which time zone is used for budgets and dates?",
        "Expense Management follows your company time zone so month and year boundaries stay consistent with your business operations.",
      ],
      [
        "Can I control what expense types employees can submit?",
        "Yes. Use Expense Types & Payment Methods in the settings menu to enable/disable categories (like Travel, Meals, Office Supplies) and manage payment methods (like Company Card or Reimbursable).",
      ],
      [
        "Do my filters stay the next time I open the page?",
        "Yes. The Expense page remembers your selected tab, filters, sort order, and page size so your view stays consistent between visits.",
      ],
      [
        "Is there a record of decisions and notes?",
        "Yes. Approvals, rejections, and change requests are tracked with the decision and your notes so there’s a clear review trail if questions come up later.",
      ],
    ],
  },

  {
    id: "employees",
    path: "/manage-employees",
    icon: <FaUsers />,
    title: "Employee Management",
    summary:
      "Add employees one by one or in bulk, organize them by department, control access, and decide who completes their own onboarding.",
    targetTab: "employee-management",
    tags: ["People", "Core", "Access", "Onboarding", "Bulk Add"],
    steps: [
      "Open the Employee Management page to see your team list. At the top, you’ll see how many employees you’re using on your plan (for example, 8/10 employees used).",
      "To add a single employee, click Add Employee. Fill in their first name, middle initial (if needed), last name, email address, job title, and employment type (Full-time or Part-time / Contract).",
      "Set a Start Date. For part-time or contract workers, you can also set an End Date or mark the role as ongoing by checking “No end date (ongoing).”",
      "Choose a Group / Department. You can pick from built-in groups (IT, Finance, Sales, Marketing, Operations) or click + Add custom… to create your own departments like “Customer Success” or “Warehouse.”",
      "If you choose the IT group, you’ll see an extra field for Help Desk Role. Pick whether this person is a Helpdesk Agent or Helpdesk Manager so they get the right Help Desk access.",
      "Use the switch for “Require employee to complete onboarding?” to decide who fills in the detailed profile. When it is ON, the employee will be guided to complete their profile after they first log in. When it is OFF, you (or another manager) will complete their profile from the employee detail page.",
      "Click Add an Employee to save. If you’re at or above your employee limit, you’ll see a message asking you to upgrade your plan before adding more people.",
      "To add several people at once, click Add Multiple Employees. Use the table to fill one row per employee: name, email, job title, type, dates, group, and onboarding. Blank rows are ignored. The same rules apply for IT (Help Desk Role required) and employee limits.",
      "Use the Status filter above the list to view All, Active, Pending, or Terminated employees. Use the search box to quickly find someone by name or email.",
      "Click on an employee row to open their detail page. From there, you can update their information, change their status (for example, from Pending to Active or Terminated), and finish any missing profile details.",
      "To completely remove someone, click Remove on their row. You’ll see a confirmation message explaining that this will revoke their access and remove them from your roster. Use this only when you’re sure you don’t need their record anymore.",
    ],
    faqs: [
      [
        "What does the employee limit (for example 10/10 employees used) mean?",
        "Your employee limit is based on your subscription plan. Once you reach that number, you won’t be able to add more employees until you upgrade your plan. You can still edit or remove existing employees.",
      ],
      [
        "What do Pending, Active, and Terminated mean?",
        "Pending is usually used for new employees who have been added but are not fully onboarded yet. Active means they are part of your current staff and can use the system. Terminated is for employees who have left the company but whose record you want to keep for history and reports.",
      ],
      [
        "What does the small dot badge next to someone’s name mean?",
        "The small badge means the profile is incomplete. Some important fields like preferred name, phone, gender, address, birth date, role, job title, group, or hire date are still missing. You or the employee (if onboarding is required) can complete these from the employee detail page.",
      ],
      [
        "What is the difference between self-onboarding and manager-completed profiles?",
        "With self-onboarding turned ON, the employee is asked to complete their own profile after they log in for the first time. With it turned OFF, you or another manager will complete their profile manually from the employee detail page. In both cases, you stay in control and can review or edit their information later.",
      ],
      [
        "How do IT and Help Desk roles work?",
        "If you assign someone to the IT group, you must choose whether they are a Helpdesk Agent or Helpdesk Manager. This decides which Help Desk screens they see and what they can do when handling support tickets.",
      ],
      [
        "Should I remove an employee or just mark them as Terminated?",
        "In most cases, it’s better to mark them as Terminated so their history (time entries, leave, expenses, training, and tickets) stays available for reports. Use the Remove button only when you’re sure you no longer need their record in your roster.",
      ],
      [
        "Can I add employees before I know all their details?",
        "Yes. You can start with basic information like name, email, job title, and group, then let the employee complete their own profile through onboarding or finish it yourself later from the employee detail page.",
      ],
    ],
  },

  {
    id: "training",
    path: "/employer-training",
    icon: <FaHeadset />,
    title: "Training Manager",
    summary:
      "Build a training library, assign courses to employees or teams, and track who’s on time, overdue, or fully compliant.",
    targetTab: "Training",
    tags: ["Learning", "Deadlines", "Progress", "Compliance", "Analytics"],
    steps: [
      // 1 – Library
      "Open the Training Manager and go to the Training Library tab. Create new courses by choosing a type (video, PDF, or external link), adding a title, description, and optional thumbnail image. Courses saved here can be reused for any employees in your company.",
      // 2 – Deadlines (TZ aware)
      "Set an optional deadline for each course if you want the system to track when items become overdue. Deadlines are stored in your company time zone so “due today” and “overdue” are calculated based on your local schedule.",
      // 3 – Assign
      "Switch to the Assign Training tab to assign courses. You can assign to individual employees, groups, or your entire company. Each assignment is linked to a specific course and due date (if set). Employees see these assignments on their Training page.",
      // 4 – Employee experience
      "Employees open their Training page to watch videos, read PDFs, or follow training links. As they work through content, they can mark items as Completed. If you verify completion another way (for example, an in-person workshop), you can also update the status from the employer side.",
      // 5 – Tracker
      "Use the Training Tracker tab to see all assignments in one place. Filter by employee, course, or status (Not Started, In Progress, Completed, Overdue) to quickly spot who is behind and which content is causing delays.",
      // 6 – Overdue & follow-up
      "Overdue assignments are clearly labeled so you can follow up with employees. You can use your notification preferences and internal communication tools to send reminders or escalate overdue compliance training.",
      // 7 – Reuse & onboarding
      "For new hires or recurring training cycles, simply reuse existing courses from the Training Library instead of recreating them. This keeps your onboarding and compliance process consistent over time.",
    ],
    faqs: [
      [
        "Which types of training content are supported?",
        "You can upload videos, PDFs, or share external links (such as a learning portal, policy site, or quiz tool). Each course can also have a thumbnail image so employees can recognize it at a glance.",
      ],
      [
        "How do deadlines work for training?",
        "When you set a deadline, the system checks whether the training is marked as Completed by that time in your company time zone. If the deadline passes and the assignment is still not completed, it appears as Overdue in both the employer’s Training Tracker and the employee’s Training page.",
      ],
      [
        "Can employees mark their own training as completed?",
        "Yes. Employees can mark a course as Completed from their Training page once they finish it. The completion date is stored and visible in the employer Training Tracker, so you can review who completed what and when.",
      ],
      [
        "Can employers update training status manually?",
        "Yes. If you confirm completion through another method (for example, an in-person workshop or passing score in another system), you can update an employee’s training status from the employer view so your records stay accurate.",
      ],
      [
        "Can I reuse courses for new employees or future groups?",
        "Absolutely. Once a course is in your Training Library, you can assign it to any employees or groups as many times as needed. This is ideal for onboarding checklists, annual compliance training, and recurring refresher sessions.",
      ],
      [
        "How do I see who is behind on training?",
        "Open the Training Tracker tab and filter by status (for example, Overdue or Not Started). You’ll see a consolidated list of employees and courses so you can identify who needs a reminder or extra support.",
      ],
      [
        "Does the Training Manager support analytics?",
        "Yes. The Training Tracker view shows how many assignments are Not Started, In Progress, Completed, or Overdue, and you can see which courses are most commonly assigned. This helps you track overall progress and spot training gaps across your company.",
      ],
    ],
  },

  {
    id: "payroll",
    path: "/admin-dashboard",
    icon: <MdOutlineAttachMoney />,
    title: "Payroll Report",
    summary:
      "Turn tracked hours and leave into a clear pay-period summary for each employee, including regular vs overtime hours and estimated gross pay, ready to export to CSV.",
    targetTab: "Payroll",
    tags: ["CSV Export", "Overtime", "Policies", "Reports"],
    steps: [
      "Open the Payroll page and click “Payroll settings” the first time you use it. Choose how often you pay employees (weekly, every 2 weeks, or twice per month).",
      "Set the first day your pay cycles are based on (for example, the Monday of your first pay week). This makes sure pay periods line up with how you already run payroll.",
      "If you use overtime, turn it on and choose the rule that matches your policy: either after a certain number of hours in the pay period (for example 40 or 80), or after more than a set number of hours in a single day (for example 8). Set your overtime rate, such as 1.5× for time-and-a-half.",
      "Back on the main Payroll page, choose whether you want to see results for all employees or just one person.",
      "Pick the pay-period start date. The end date fills in automatically based on the pay schedule you chose (weekly, bi-weekly, or semi-monthly).",
      "Click “Generate report”. The system pulls in hours from Time Tracking and approved leave for that date range, and combines them with each employee’s hourly pay.",
      "Review the table: you’ll see worked hours, paid leave, unpaid leave, total payable hours, regular hours, overtime hours, hourly rate, and estimated gross pay for each employee.",
      "Check the summary bar at the top to see totals across the whole period, including total payable hours, total overtime hours, and total estimated gross pay.",
      "When you’re ready, click “Export CSV” to download a file you can send to your payroll provider or import into your payroll system.",
    ],
    faqs: [
      [
        "What do I need to set up before using the Payroll report?",
        "Make sure employees have an hourly pay rate saved on their profile, and that you are already using Time Tracking and Leave so hours and time off are being recorded. Then complete the Payroll settings once to match your real-world pay schedule and overtime rules.",
      ],
      [
        "Which hours are included in the report?",
        "The report includes worked hours from Time Tracking and any leave that has been recorded as paid time off. Unpaid leave is shown for context but does not count toward payable hours or pay.",
      ],
      [
        "How does overtime work in this report?",
        "If overtime is turned on, the system looks only at worked hours (not paid leave). Depending on your choice, overtime starts either after an employee passes your total-hours limit for the pay period (for example 40 or 80 hours) or after they work more than your daily limit in a single day (for example more than 8 hours). Those extra hours are counted as overtime and paid at the overtime rate you set.",
      ],
      [
        "What does “Estimated gross pay” mean?",
        "Estimated gross pay is a simple calculation: regular worked hours at the normal rate, plus overtime hours at the overtime rate, plus paid leave hours at the normal rate. It does not include taxes, benefits, or any extra bonuses or deductions you may handle in your payroll system.",
      ],
      [
        "Where does the hourly rate come from?",
        "From each employee’s profile in Oiiko. Whatever hourly pay you’ve entered there is what the Payroll report uses for its calculations. If no rate is set, that employee’s pay will show as zero even if they have hours.",
      ],
      [
        "Why does the report ask for a pay-period start date?",
        "The start date tells Oiiko exactly which dates to include in the report. Based on that and your pay schedule, the app figures out the correct end date so the report matches how you actually run payroll.",
      ],
      [
        "Which time zone is used?",
        "All dates and times use the company time zone you set in Settings. That way, Payroll lines up with the same dates and hours you see in Scheduling and Time Tracking.",
      ],
      [
        "Can I change my settings later?",
        "Yes. You can update your pay schedule or overtime rules at any time. New reports will always use the latest settings. If you need an exact record of how a past payroll was calculated, download and keep the CSV export for that period.",
      ],
    ],
  },

  {
    id: "analytics",
    path: "/analytics",
    icon: <FaChartBar />,
    title: "Reports & Analytics",
    summary:
      "See dashboards for employees, time tracking, scheduling, training, and leave so you can spot trends and make better decisions.",
    targetTab: "analytics",
    tags: ["Reports", "Insights"],
    steps: [
      "Open the Analytics page from your dashboard. At the top, use the dropdown menu to switch between Employee, Time Tracking, Scheduling, Training, and Leave dashboards.",
      "The dashboards you see depend on your subscription plan and which features your company uses. For example, if you don’t use Training, you won’t see training charts.",
      "Each dashboard shows key numbers (such as totals, percentages, and trends) plus visual charts so you can quickly understand what’s happening in that area.",
      "Use the date filters or other controls (when available) to focus on a specific time range or slice of data.",
      "Review your results regularly to spot patterns: overtime increasing, certain teams with more leave, training courses that are often overdue, and more. Use these insights to adjust schedules, policies, or budgets.",
    ],
    faqs: [
      [
        "What does each dashboard show?",
        "The Employee dashboard highlights headcount, active vs inactive employees, roles, departments, and basic diversity metrics such as age and tenure. Time Tracking focuses on hours worked, who is working the most, and late clock-in percentages. Scheduling shows scheduled hours, busiest days, and which employees are scheduled most often. Training shows assigned vs completed courses and overdue counts. Leave shows which types of leave are most common and which months are the busiest.",
      ],
      [
        "Can I export charts or data from Analytics?",
        "The Analytics page is designed for quick visual insight inside Oiiko. For deeper analysis or exports, you can use the CSV exports available in modules like Time Tracking, Scheduling, and Payroll, or work with your own reporting tools alongside Oiiko.",
      ],
      [
        "Does Analytics update in real time?",
        "Analytics uses your live company data and refreshes regularly. When you add new employees, approve leave, or adjust schedules and training, you’ll see those changes reflected as the system updates.",
      ],
    ],
  },

  {
    id: "tickets",
    path: "/submitted-tickets",
    icon: <FaHeadset />,
    title: "Help Desk",
    summary:
      "Employees create IT or support tickets with screenshots, you track progress, and your team works against clear response and resolution targets (SLAs).",
    targetTab: "Tickets",
    tags: ["Support", "SLA", "Triage"],
    steps: [
      "Employees open the Help Desk page and submit a ticket with a title, description, category (such as Hardware, Software, Network, or General Help), priority, impact, and urgency. They can attach screenshots or other images to show what’s wrong.",
      "Each ticket gets its own incident number and starts in an Open status. Behind the scenes, timers are started for your “first response” and “resolution” targets based on your SLA settings.",
      "Employees can switch to Ticket History to see all of their tickets, filter by status (All, Open, In Progress, Closed), read any comments from your team, and reopen tickets if needed.",
      "Admins and help desk agents can switch to the full ticket management view. From there, they can filter by status, priority, impact, the person who submitted the ticket, or who is currently working on it.",
      "Agents can claim tickets so it’s clear who is responsible. They can update the status (for example, from Open to In Progress to Closed) and leave comments that the employee can read.",
      "SLA indicators show whether a ticket is on track, close to breaching, or already past your agreed response or resolution times. This helps your team prioritize which tickets to handle first.",
      "Managers can view simple dashboards or summary lists that highlight tickets needing attention, such as high-priority issues or tickets that are close to or past their SLA targets.",
    ],
    faqs: [
      [
        "Which fields are required when submitting a ticket?",
        "Employees must provide a title, description, category, priority, impact, and urgency. Attachments such as screenshots are optional but recommended for technical issues.",
      ],
      [
        "What ticket statuses are used?",
        "Typical statuses include Open, In Progress, and Closed. You may also see states like “My Tickets” or “Unassigned” in the agent view to help your team triage work.",
      ],
      [
        "How do SLAs work in the Help Desk?",
        "SLAs (service level agreements) are your internal targets for how quickly you respond to and resolve tickets. The system tracks how long each ticket has been waiting for a first response and how long it stays open. Tickets that are nearing or past your targets are clearly marked so agents can prioritize them.",
      ],
      [
        "Can employees see the full ticket history?",
        "Yes. Employees can see the status of each ticket, all comments from the help desk, and any attachments. This reduces back-and-forth emails and keeps the conversation in one place.",
      ],
      [
        "How often is the ticket list updated?",
        "The ticket list refreshes regularly so agents and employees see up-to-date information without having to reload the page manually.",
      ],
    ],
  },

  {
    id: "employer-profile",
    path: "/employer-profile",
    icon: <FaUserTie />,
    title: "Employer Profile",
    summary:
      "Update your company information, contact details, address, and logo so your account matches your brand and records.",
    targetTab: "employer-profile",
    tags: ["People", "Admin"],
    steps: [
      "Open the Employer Profile page from your main dashboard. You’ll see your owner details, company information, and address.",
      "Click Edit to make changes. You can update the owner name, phone number, and other contact details used for communication and billing.",
      "Update your company information such as the company name, industry, and website so it matches how you present your business externally.",
      "Use the address fields to set your main business location, including street, city, state or province, postal code, and country. This is used for display and can support region-specific settings later.",
      "Upload a company logo so your brand shows up on your dashboard and in other parts of the app. You’ll see a preview before you save.",
      "When you’re done, click Save. Your changes are applied across Oiiko wherever this information is used.",
    ],
    faqs: [
      [
        "Who can edit the Employer Profile?",
        "Only users with the proper admin or owner role can edit the Employer Profile. Regular employees do not have access to this page.",
      ],
      [
        "Where will my logo appear?",
        "Your logo typically appears in the top navigation, on the employer dashboard, and in other areas where the company is referenced, giving your account a more branded look.",
      ],
      [
        "Can I change my company size or creation date?",
        "Company size and creation date are usually set when your account is first created. If they need to be changed for a specific reason, contact support so we can review and update them.",
      ],
      [
        "What is the “Display Location” field?",
        "Display Location is a read-only line that combines your city, state or province, and country into a simple label that appears elsewhere in the app.",
      ],
    ],
  },

  {
    id: "notifications",
    path: "/Settings",
    icon: <FaBell />,
    title: "Notifications",
    summary:
      "Choose which alerts you want to receive so you’re notified about the things that matter most in your account.",
    targetTab: null,
    navState: { tab: "notifications" },
    tags: ["Alerts", "Admin", "Plans"],
    steps: [
      "Go to Settings and open the Notifications section. You’ll see a list of possible alerts that Oiiko can send you.",
      "Use the main switch or button to turn notifications on or off globally for your account. When notifications are off, you won’t receive any alerts from Oiiko.",
      "Underneath, use the checkboxes to choose specific types of alerts you want to receive, such as late clock-ins, missed shifts, leave requests, training reminders, new tickets, platform updates, or expenses submitted.",
      "Click Save Preferences when you’re done. Your choices are saved to your profile so you receive only the alerts you care about.",
      "Some alert types are only available on certain subscription plans. If you don’t see an option listed, it may be part of a higher-tier plan.",
    ],
    faqs: [
      [
        "What types of notifications can I enable?",
        "Common alerts include late clock-ins, missed shifts, new leave requests, missed training deadlines, training completions, profile updates by employees, new help desk tickets, new expense submissions, and general platform updates such as new features or maintenance announcements.",
      ],
      [
        "How does my plan affect notifications?",
        "Your plan determines which notification types are available. For example, Basic might include core workforce alerts, Standard adds leave and expenses, Premium adds training and help desk alerts, and standalone Training or Helpdesk plans focus on their specific modules.",
      ],
      [
        "Can I change my notification settings later?",
        "Yes. You can return to the Notifications page at any time to turn alerts on or off or change which types you receive.",
      ],
      [
        "Which time zone is used for timestamps in notifications?",
        "All notification timestamps follow your company time zone, which you set under Settings. This keeps alerts in sync with your schedules and reports.",
      ],
    ],
  },

  {
    id: "job-sites",
    path: "/Settings",
    icon: <FaMapMarkerAlt />,
    title: "Job Sites",
    summary:
      "Add and manage work locations (job sites) so schedules and staff planning can be tied to the right place.",
    targetTab: null,
    navState: { tab: "locations" },
    tags: ["Locations", "Scheduling", "Admin", "Addresses"],
    steps: [
      "Open Settings and go to the Job Sites section.",
      "You’ll see your current locations list. Your Primary location is created during setup and can’t be deactivated or deleted from here.",
      "Click Add Location to create a new job site. Enter a location name and the address details.",
      "Use Edit to update the name or address for a site (non-primary sites).",
      "Use Deactivate to temporarily hide a site from selection while keeping the history available.",
      "Turn on Show inactive to view and reactivate previously deactivated job sites.",
      "When job sites are enabled in Scheduling, you’ll pick a job site during schedule creation so the schedule is clearly tied to a location.",
    ],
    faqs: [
      [
        "What’s the difference between Primary and Site?",
        "Primary is your main company location created during setup. Sites are additional job locations you add for scheduling and planning.",
      ],
      [
        "Can I deactivate the Primary location?",
        "No. Primary location is locked for safety and consistency. You can add additional sites and deactivate those as needed.",
      ],
      [
        "Will deactivating a job site delete past schedules?",
        "No. Deactivating only hides it from active use. Past schedules and records still keep their location history.",
      ],
      [
        "Where do job sites show up?",
        "Job sites appear in Scheduling (during schedule creation) and anywhere else you choose to reference work location in the platform.",
      ],
    ],
  },

  {
    id: "settings",
    path: "/Settings",
    icon: <FaCogs />,
    title: "Settings & Billing",
    summary:
      "Manage your login details, time zone, notification preferences, legal policy, and subscription plan in one place.",
    targetTab: null,
    navState: { tab: "account" },
    tags: [
      "Security",
      "Admin",
      "Billing",
      "Notifications",
      "Job Sites",

      "Time Zone",
      "Privacy",
    ],
    steps: [
      // 1 – Layout
      "Open the Settings page from your dashboard menu. You’ll see tabs on the left for Account Settings, Notifications, Privacy Policy, and Billing.",
      // 2 – Account: password + email
      "In the Account Settings tab, you can change your password and update your email address. For security, your current password is required when changing your password, and changes may sign you out on some devices.",
      // 3 – Account: time zone
      "Still in Account Settings, you can update your company time zone. This setting controls how schedules, time tracking, deadlines, leave, expenses, and reports are displayed across your company. When you switch time zones, Oiiko asks for confirmation before applying the change.",
      // 4 – Account: delete account
      "The Account tab also includes a Danger Zone section where you can permanently delete your account. You must confirm your password and acknowledge that this action is permanent before proceeding.",
      // 5 – Notifications
      "In the Notifications tab, you can turn all notifications on or off and choose which specific alerts you want to receive—such as late clock-ins, leave requests, training events, new tickets, platform updates, and expense submissions. Some notification types are only available on certain plans.",
      "In the Job Sites tab, you can add, edit, deactivate, and reactivate work locations. These locations can be used during Scheduling so shifts are tied to the correct site.",

      // 6 – Privacy Policy
      "In the Privacy Policy tab, you can read Oiiko’s combined Terms of Use, Privacy Policy, and Data Processing Addendum (DPA). This explains what data is collected, how it is used, and your rights as an employer and as a data controller.",
      // 7 – Billing overview
      "In the Billing tab, you can view your current subscription plan, subscription status (trialing, active, cancelling), the start date, and your current billing period end date. You’ll also see a list of the major features included in your plan (for example, Time Tracking, Scheduling, Leave, Expenses, Training, and Help Desk).",
      // 8 – Manage subscription
      "From the Billing tab, click Manage Subscription to open the Subscription page. There you can upgrade, change plans, or adjust your payment details.",
      // 9 – Cancel subscription
      "The Billing tab also includes a Cancel Subscription section. When you cancel, Oiiko asks a few optional feedback questions and then schedules the cancellation at the end of your trial or current billing period. You keep access until that date and can see when the subscription will end.",
    ],
    faqs: [
      [
        "Do I need to re-enter my password to change it?",
        "Yes. For your security, Oiiko requires your current password before allowing you to set a new one. After your password is updated, you may be asked to sign in again on some devices.",
      ],
      [
        "Does changing the company time zone affect everyone?",
        "Yes. The time zone is a company-level setting. Changing it updates how times and dates appear across schedules, time tracking, training deadlines, leave dates, expense dates, and analytics for all users in your company.",
      ],
      [
        "Where are my personal and company settings stored?",
        "Your personal settings—such as your notification preferences and user email—are stored on your user profile. Company-level settings, like the company time zone, subscription plan, and employee limits, are stored under your company record.",
      ],
      [
        "What does the Billing section show?",
        "The Billing tab shows your plan name, whether the subscription is in trial or active, the current billing period, and a list of included features. It also shows if cancellation is scheduled and the date your access will end. From there you can open the Subscription page to upgrade or manage your plan.",
      ],
      [
        "What happens when I cancel my subscription?",
        "When you cancel, your subscription is set to end at the conclusion of your trial or current billing period. You keep access until that date. Billing status is updated to indicate the upcoming cancellation, and you can see the planned end date in the Billing tab.",
      ],
      [
        "What’s the difference between deleting my account and cancelling my subscription?",
        "Cancelling your subscription stops future billing but keeps your account and data available until the end of the current period. Deleting your account from the Danger Zone section is a separate, permanent action that removes your account data and access to Oiiko. Deletion cannot be undone.",
      ],
      [
        "Why do I see different notification options on different plans?",
        "Some alerts, such as training or help desk notifications, are tied to features that only exist on certain plans. The Notifications tab automatically shows only the notification types that apply to the features included in your current plan.",
      ],
      [
        "Can other people in my company change these settings?",
        "Only users with access to the Settings page for your company (typically the primary employer or account owner) can change company-level settings like time zone, plan, or cancellation. Individual users can adjust their own notification preferences where allowed.",
      ],
    ],
  },
];

/** Contact Support Modal */
function SupportModal({
  open,
  onClose,
  values,
  setValues,
  onSubmit,
  submitting,
}) {
  if (!open) return null;
  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="hc-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="hc-modal" onClick={stop}>
        <div className="hc-modal-head">
          <h3>Contact Support</h3>
          <button className="hc-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="hc-modal-body">
          <label className="hc-field">
            <span>Email</span>
            <input
              type="email"
              value={values.email}
              onChange={(e) =>
                setValues((s) => ({ ...s, email: e.target.value }))
              }
              placeholder="you@company.com"
            />
          </label>

          <div className="hc-grid-2">
            <label className="hc-field">
              <span>Category</span>
              <select
                value={values.category}
                onChange={(e) =>
                  setValues((s) => ({ ...s, category: e.target.value }))
                }
              >
                <option>General</option>
                <option>Login</option>
                <option>Billing</option>
                <option>Time Tracking</option>
                <option>Scheduling</option>
                <option>Leave</option>
                <option>Training</option>
                <option>Expenses</option>
                <option>Help Desk</option>
                <option>Other</option>
              </select>
            </label>

            <label className="hc-field">
              <span>Priority</span>
              <select
                value={values.priority}
                onChange={(e) =>
                  setValues((s) => ({ ...s, priority: e.target.value }))
                }
              >
                <option>Normal</option>
                <option>High</option>
                <option>Low</option>
              </select>
            </label>
          </div>

          <label className="hc-field">
            <span>Subject</span>
            <input
              value={values.subject}
              onChange={(e) =>
                setValues((s) => ({ ...s, subject: e.target.value }))
              }
              placeholder="Brief subject"
            />
          </label>

          <label className="hc-field">
            <span>Details</span>
            <textarea
              rows={6}
              value={values.details}
              onChange={(e) =>
                setValues((s) => ({ ...s, details: e.target.value }))
              }
              placeholder="Describe what happened, steps to reproduce, any error messages…"
            />
          </label>

          <p className="hc-hint">
            This sends a ticket to the Oiiko dev inbox. We’ll reply to the email
            above.
          </p>
        </div>

        <div className="hc-modal-foot">
          <button
            className="hc-btn ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="hc-btn primary"
            onClick={onSubmit}
            disabled={submitting}
          >
            {submitting ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ✅ Export topics & plan access so GlobalSearch can reuse them
export const HELP_TOPICS = TOPICS;
export const HELP_PLAN_TOPIC_ACCESS = PLAN_TOPIC_ACCESS;

export default function Help({ onTabChange }) {
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [tag, setTag] = useState("All");
  const [tourOpen, setTourOpen] = useState(false);

  const [supportOpen, setSupportOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [support, setSupport] = useState({
    email: "",
    subject: "",
    category: "General",
    priority: "Normal",
    details: "",
  });

  const { user } = useAuth();
  const navigate = useNavigate();

  const { subscription } = useSubscription();
  const planName = subscription?.planName || "Basic";
  const VISIBLE_TOPICS = useMemo(() => {
    const allowed = allowedIdsForPlan(planName);
    return TOPICS.filter((t) => allowed.has(t.id));
  }, [planName]);

  // Stable opener to avoid steps array churn
  const handleOpenSidebar = useCallback(() => {
    // Prefer an explicit attribute if your sidebar exposes it:
    const expanded = document.querySelector(
      "#app-sidebar[aria-expanded='true']"
    );
    const openByClass =
      document.body.classList.contains("sidebar-open") ||
      document.documentElement.classList.contains("sidebar-open");

    if (expanded || openByClass) return; // already open, do nothing

    document.querySelector("#sidebar-toggle-btn")?.click();
  }, []);

  // Prefill email if signed in
  useEffect(() => {
    if (user?.email)
      setSupport((s) => ({ ...s, email: s.email || user.email }));
  }, [user?.email]);

  const allTags = useMemo(() => {
    const t = new Set(["All"]);
    VISIBLE_TOPICS.forEach((x) => x.tags?.forEach((y) => t.add(y)));
    return Array.from(t);
  }, [VISIBLE_TOPICS]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();

    return VISIBLE_TOPICS.filter((t) => {
      const text = (
        t.title +
        " " +
        t.summary +
        " " +
        t.steps.join(" ") +
        " " +
        t.faqs.flat().join(" ") +
        " " +
        (t.tags || []).join(" ")
      ).toLowerCase();
      const matchesText = !term || text.includes(term);
      const matchesTag = tag === "All" || t.tags?.includes(tag);
      return matchesText && matchesTag;
    });
  }, [q, tag, VISIBLE_TOPICS]);

  const goToTab = (tab) => {
    if (onTabChange) onTabChange(tab);
    else navigate("/admin-dashboard", { replace: false, state: { tab } });
  };

  const handleTry = (topic) => {
    if (onTabChange && topic.targetTab) return goToTab(topic.targetTab);
    if (topic.path)
      return navigate(topic.path, { state: topic.navState || {} });
    if (topic.targetTab) return goToTab(topic.targetTab);
  };

  /** Write to a single, top-level collection so devs can see all requests */
  const CONTACT_COLLECTION = "contact_requests";

  const handleSupportSubmit = useCallback(async () => {
    if (!support.email?.trim()) {
      toast.error("Please provide a contact email.");
      return;
    }
    if (!support.subject?.trim() && !support.details?.trim()) {
      toast.error("Please add a subject or some details.");
      return;
    }

    setSubmitting(true);
    try {
      // Try to resolve companyId for context
      let companyId = user?.companyId || null;
      if (!companyId && user?.uid) {
        const uref = doc(db, "users", user.uid);
        const usnap = await getDoc(uref);
        companyId = usnap.data()?.companyId || null;
      }

      const payload = {
        email: support.email.trim(),
        subject: support.subject.trim() || "(no subject)",
        category: support.category,
        priority: support.priority,
        details: support.details.trim(),
        status: "open", // open | in_progress | closed
        page: window.location.pathname,
        browser: navigator.userAgent,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        companyId: companyId, // helps devs filter by company
      };

      await addDoc(collection(db, CONTACT_COLLECTION), payload);

      setSupportOpen(false);
      setSupport((s) => ({ ...s, subject: "", details: "" }));
      toast.success("Support request submitted. We’ll follow up by email.");
    } catch (err) {
      console.error("Support submit failed:", err);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [support, user?.uid, user?.companyId]);

  return (
    <div className="hc-wrap">
      <ToastContainer position="top-right" />

      {/* Hero */}
      <header className="hc-hero">
        <div className="hc-hero-bg" />
        <div className="hc-hero-content">
          <h1>Help Center</h1>
          <p>
            Quick guides for every module. Search, filter, and jump straight
            into the feature.
          </p>
          <div className="hc-search">
            <FaSearch />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search time tracking, scheduling, leave, expenses…"
              aria-label="Search help topics"
            />
          </div>
          <div className="hc-chips" role="tablist" aria-label="Help filters">
            {allTags.map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={t === tag}
                className={`hc-chip ${t === tag ? "active" : ""}`}
                onClick={() => setTag(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Cards */}
      <section className="hc-grid">
        {results.map((t) => {
          const isOpen = openId === t.id;
          return (
            <article key={t.id} className={`hc-card ${isOpen ? "open" : ""}`}>
              <div className="hc-card-border" />
              <div className="hc-card-body">
                <div className="hc-card-head">
                  <div className="hc-icon">{t.icon}</div>
                  <div className="hc-meta">
                    <h3>{t.title}</h3>
                    <p>{t.summary}</p>
                  </div>
                </div>

                {t.tags?.length ? (
                  <div className="hc-tagline">
                    {t.tags.map((tg) => (
                      <span key={tg} className="hc-tag">
                        {tg}
                      </span>
                    ))}
                  </div>
                ) : null}

                <div className="hc-actions">
                  <button
                    className="hc-btn ghost"
                    onClick={() => setOpenId(isOpen ? null : t.id)}
                    aria-expanded={isOpen}
                    aria-controls={`details-${t.id}`}
                  >
                    {isOpen ? "Hide guide" : "Quick guide"}
                  </button>
                  <button
                    className="hc-btn primary"
                    onClick={() => handleTry(t)}
                  >
                    Try it →
                  </button>
                </div>

                {isOpen && (
                  <div className="hc-details" id={`details-${t.id}`}>
                    <h4>How it works</h4>
                    <ol className="hc-steps">
                      {t.steps.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>

                    {t.faqs?.length ? (
                      <>
                        <h4>FAQs</h4>
                        <ul className="hc-faqs">
                          {t.faqs.map(([fq, ans], i) => (
                            <li key={i}>
                              <div className="q">{fq}</div>
                              <div className="a">{ans}</div>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </section>

      {/* Footer CTA */}
      <footer className="hc-footer">
        <div className="hc-footer-inner">
          <div>
            <strong>Still stuck?</strong>
            <div className="muted">
              Send a support request to the Oiiko dev team.
            </div>
          </div>
        </div>
        <div
          className="hc-hero-actions"
          style={{ marginTop: 12, display: "flex", gap: 8 }}
        >
          <button className="hc-btn outline" onClick={() => setTourOpen(true)}>
            Start Guided Tour
          </button>
          <button
            className="hc-btn outline"
            onClick={() => setSupportOpen(true)}
          >
            Contact Support
          </button>
        </div>
      </footer>

      {/* Support modal (writes to /contact_requests) */}
      <SupportModal
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        values={support}
        setValues={setSupport}
        onSubmit={handleSupportSubmit}
        submitting={submitting}
      />

      <WelcomeTour
        open={tourOpen}
        companyName={user?.companyName || ""}
        planName={subscription?.planName || "Basic"}
        onJumpTab={(tab) => {
          if (onTabChange) onTabChange(tab);
          else navigate("/admin-dashboard", { replace: false, state: { tab } });
        }}
        onOpenSidebar={handleOpenSidebar}
        onClose={() => setTourOpen(false)}
      />
    </div>
  );
}
